/**
 * The receipt's face, Manrope, loaded before anything is drawn in it.
 *
 * Two consumers and two reasons. `drawSlip` sets text on a canvas, and a canvas
 * does not wait for a web font: asked for a face that has not arrived, it draws
 * in the fallback and says nothing, so the first receipt of the day came out in
 * system-ui. And `renderHtml` prints through a `srcdoc` iframe that shares no
 * loaded fonts with this page, so it is handed the rules themselves.
 *
 * `?inline` is what gives both: the stylesheet as a string, with its `url()`s
 * already rewritten to the hashed files Vite emitted — the ones the service
 * worker keeps as immutable assets.
 */
import fontCss from '@fontsource-variable/manrope/index.css?inline'

export const RECEIPT_FONT_CSS: string = fontCss

/**
 * How long a receipt waits for the face. A slip in the fallback is still a
 * receipt; a customer standing at a kiosk that is waiting on a font is not.
 */
const PATIENCE_MS = 1500

let ready: Promise<void> | null = null

/** Resolves when Manrope is loaded, or when waiting stopped being worth it. Never rejects. */
export function receiptFontReady(): Promise<void> {
  ready ??= (async () => {
    const style = document.createElement('style')
    style.textContent = fontCss
    document.head.appendChild(style)
    // Ө and Ү name the Cyrillic-ext subset, which is the file that carries them.
    await Promise.race([
      Promise.all([
        document.fonts.load("500 18px 'Manrope Variable'", 'АӨҮa'),
        document.fonts.load("800 36px 'Manrope Variable'", 'АӨҮa')
      ]),
      new Promise((resolve) => setTimeout(resolve, PATIENCE_MS))
    ])
  })().catch(() => {
    // No FontFace API, a file that would not load: the fallback face prints.
  })
  return ready
}
